/*
 * WELLF - unified Klaviyo form handler + lightweight modal controller.
 *
 * One module, loaded on every page. It is entirely data-attribute driven so the
 * same code wires every signup form on the site without per-form JS.
 *
 * FORMS
 *   Any <form data-klaviyo-form data-klaviyo-list="LISTID"> is intercepted on submit.
 *   The form's email field (input[type=email] or [name=email]) is read and the
 *   profile is created/updated and subscribed to LISTID via Klaviyo's public,
 *   CORS-enabled client endpoint (single opt-in is enforced by the list settings
 *   in Klaviyo, not here). Optional attributes:
 *     data-klaviyo-source   -> custom_source recorded on the subscription
 *     data-success-message  -> text shown in [data-klaviyo-message] on success
 *     data-error-message    -> text shown on failure
 *   State is reflected as is-loading / is-success / is-error classes on the form.
 *
 * MODALS
 *   <button data-wellf-modal-open="lab-report"> opens <dialog id="wellf-modal-lab-report">.
 *   [data-wellf-modal-close] or a backdrop click closes it (Esc is native <dialog>).
 *
 * Global config is injected by layout/theme.liquid as window.WELLF_KLAVIYO.
 */
(function () {
  'use strict';

  var CFG = window.WELLF_KLAVIYO || {};
  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

  function setState(form, state, message) {
    form.classList.remove('is-loading', 'is-success', 'is-error');
    if (state) form.classList.add('is-' + state);
    // Message slot is usually inside the form; some layouts (e.g. the footer)
    // place it as a sibling, so fall back to the form's parent.
    var slot = form.querySelector('[data-klaviyo-message]');
    if (!slot && form.parentElement) slot = form.parentElement.querySelector('[data-klaviyo-message]');
    if (slot && message != null) slot.textContent = message;
  }

  // opts: either a source string (back-compat) or { source, properties }.
  // properties are written onto the Klaviyo profile so flows can branch on them
  // (e.g. the spin-to-win prize -> spin_prize / spin_prize_label).
  function subscribe(listId, email, opts) {
    if (!CFG.companyId) {
      return Promise.reject(new Error('Missing Klaviyo company id'));
    }
    if (typeof opts === 'string') opts = { source: opts };
    opts = opts || {};
    var profileAttrs = { email: email };
    if (opts.properties && Object.keys(opts.properties).length) {
      profileAttrs.properties = opts.properties;
    }
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.source || 'WELLF storefront',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttrs
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(CFG.companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: CFG.revision || '2024-10-15'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      // The client endpoint returns 202 Accepted on success.
      if (res.status === 202 || res.ok) return true;
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t);
      });
    });
  }

  // Any data-klaviyo-prop-<name>="value" on the form becomes a profile property.
  function collectProps(form) {
    var props = {};
    var attrs = form.attributes;
    for (var i = 0; i < attrs.length; i++) {
      var n = attrs[i].name;
      if (n.indexOf('data-klaviyo-prop-') === 0) {
        var key = n.slice('data-klaviyo-prop-'.length);
        if (key) props[key] = attrs[i].value;
      }
    }
    return props;
  }

  function handleSubmit(form) {
    var listId = form.getAttribute('data-klaviyo-list');
    var emailField = form.querySelector('input[type="email"], input[name="email"]');
    var email = emailField ? emailField.value.trim() : '';

    if (!listId) {
      console.error('[WELLF] form is missing data-klaviyo-list', form);
      setState(form, 'error', form.getAttribute('data-error-message') || 'Something went wrong. Try again?');
      return;
    }
    if (!email) {
      if (emailField) emailField.focus();
      return;
    }

    setState(form, 'loading', '');
    subscribe(listId, email, { source: form.getAttribute('data-klaviyo-source'), properties: collectProps(form) })
      .then(function () {
        setState(form, 'success', form.getAttribute('data-success-message') || "You're in. ✓");
        if (emailField) {
          emailField.value = '';
          emailField.blur();
        }
      })
      .catch(function (err) {
        console.error('[WELLF] Klaviyo subscribe failed:', err);
        setState(form, 'error', form.getAttribute('data-error-message') || 'Hmm, that didn’t work. Try again?');
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-klaviyo-form]');
    if (!form) return;
    e.preventDefault();
    handleSubmit(form);
  });

  // ── Modal control ──
  // A fixed-overlay <div> (not a native <dialog>): opening never moves the
  // scroll position, so the button opens the modal in place.
  // An opener can carry the content the modal should show, which is how one shared
  // modal serves several triggers. Used by the Club perk thumbnails: the image on the
  // page is small and CSS-cropped, so each thumbnail hands over a full size URL rather
  // than letting the reduced version be blown up.
  function fillFromOpener(m, opener) {
    var src = opener.getAttribute('data-modal-img');
    if (src) {
      var img = m.querySelector('[data-modal-img-target]');
      if (img && img.getAttribute('src') !== src) {
        img.setAttribute('src', src);
        img.setAttribute('alt', opener.getAttribute('data-modal-img-alt') || '');
      }
    }
    var caption = opener.getAttribute('data-modal-caption');
    if (caption) {
      var cap = m.querySelector('[data-modal-caption-target]');
      if (cap) cap.textContent = caption;
    }
  }

  function openModal(id, opener) {
    var m = document.getElementById('wellf-modal-' + id);
    if (!m) return;
    if (opener) fillFromOpener(m, opener);
    m.hidden = false;
    m.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var input = m.querySelector('input[type="email"]');
    if (input) setTimeout(function () { input.focus(); }, 80);
  }
  function closeModal(m) {
    m.hidden = true;
    m.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-wellf-modal-open]');
    if (opener) {
      e.preventDefault();
      openModal(opener.getAttribute('data-wellf-modal-open'), opener);
      return;
    }
    var closer = e.target.closest('[data-wellf-modal-close]');
    if (closer) {
      var owner = closer.closest('[data-wellf-modal]');
      if (owner) closeModal(owner);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      document.querySelectorAll('[data-wellf-modal]:not([hidden])').forEach(closeModal);
    }
  });

  // Reusable API for bespoke flows that manage their own UI (e.g. the spin wheel):
  // WellfKlaviyo.subscribe(listId, email, { source, properties }) -> Promise.
  // ── OPEN API + OVERLAY STATE ──
  // openModal was closure-private, so the only way to open the welcome modal from code was
  // to synthesise a click on its trigger. That would ALSO trip the listener in
  // wellf-events.js and log wellf_welcome_line_click, making an automatic open
  // indistinguishable from someone actually finding and tapping the line. The funnel is the
  // only way we can judge whether the auto-open works, so it must not be polluted by the
  // auto-open itself. Hence a real entry point.
  //
  // anyOverlayOpen() is deliberately broader than this file's own modals. Horizon's cart
  // drawer, quick-add and image zoom are not [data-wellf-modal], and putting the £5 modal on
  // top of any of them would be worse than never showing it. The body-overflow check is the
  // one signal every drawer in this theme sets, whoever owns it.
  function anyOverlayOpen() {
    try {
      if (document.querySelector('[data-wellf-modal]:not([hidden])')) return true;
      if (document.querySelector('dialog[open]')) return true;
      if (document.body && document.body.style.overflow === 'hidden') return true;
      return false;
    } catch (e) { return true; }   // fail closed: unsure means do not stack a modal
  }

  window.WellfKlaviyo = {
    subscribe: subscribe,
    openModal: function (id) { openModal(id, null); },
    anyOverlayOpen: anyOverlayOpen
  };
})();
